import { captureMessage, withScope } from '@sentry/react';
import CaptchaEvent from './Captcha.types';

function reportEmptyCaptchaSubmit(context: Record<string, unknown>): void {
    try {
        withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('feature', 'login');
            scope.setTag('login_empty_submit', 'true');
            scope.setExtras(context);
            captureMessage('Login submit with empty fields but a solved captcha token');
        });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function initFormSubmitInterceptor(form: HTMLFormElement, captchaInput: HTMLInputElement): void {
    let verified = false;
    let pending = false;
    let abortController: AbortController | null = null;
    let restoredFromBfcache = false;
    let reported = false;

    const reportIfAnomalous = (event: SubmitEvent): void => {
        if (reported) {
            return;
        }

        const formData = new FormData(form);
        const captchaTokenLength = String(formData.get('captcha') ?? '').length;
        const usernameLength = String(formData.get('username') ?? '').length;
        const passwordLength = String(formData.get('password') ?? '').length;

        if (captchaTokenLength === 0 || (usernameLength > 0 && passwordLength > 0)) {
            return;
        }

        reported = true;
        reportEmptyCaptchaSubmit({
            csrfTokenLength: String(formData.get('csrf_token') ?? '').length,
            hasUsername: usernameLength > 0,
            hasPassword: passwordLength > 0,
            captchaTokenLength,
            restoredFromBfcache,
            verified,
            eventType: event.type,
            visibilityState: document.visibilityState,
            userAgent: navigator.userAgent
        });
    };

    form.addEventListener(
        'submit',
        (e: SubmitEvent) => {
            reportIfAnomalous(e);

            if (verified) {
                return;
            }

            e.preventDefault();
            e.stopImmediatePropagation();

            if (pending) {
                return;
            }

            pending = true;
            abortController = new AbortController();
            const { signal } = abortController;

            const resetPending = (): void => {
                pending = false;
                abortController?.abort();
                abortController = null;
            };

            form.addEventListener(
                CaptchaEvent.SUCCESS,
                () => {
                    verified = true;
                    resetPending();
                    form.requestSubmit();
                },
                { once: true, signal }
            );

            form.addEventListener(CaptchaEvent.ERROR, resetPending, { once: true, signal });
            form.addEventListener(CaptchaEvent.EXPIRE, resetPending, { once: true, signal });

            captchaInput.dispatchEvent(new CustomEvent(CaptchaEvent.EXECUTE));
        },
        { capture: true }
    );

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
            restoredFromBfcache = true;
        }
    });
}

function initSocialLoginInterceptor(form: HTMLFormElement, captchaInput: HTMLInputElement): void {
    form.querySelectorAll<HTMLAnchorElement>('[data-role="social-login"]').forEach((anchor) => {
        const originalHref = anchor.href;

        anchor.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();

            const abortController = new AbortController();
            const { signal } = abortController;

            form.addEventListener(
                CaptchaEvent.SUCCESS,
                (event: Event) => {
                    const { token } = (event as CustomEvent<{ token: string }>).detail;
                    abortController.abort();

                    const url = new URL(originalHref);
                    url.searchParams.set('token', token);
                    window.location.href = url.toString();
                },
                { once: true, signal }
            );

            form.addEventListener(CaptchaEvent.ERROR, () => abortController.abort(), {
                once: true,
                signal
            });
            form.addEventListener(CaptchaEvent.EXPIRE, () => abortController.abort(), {
                once: true,
                signal
            });

            captchaInput.dispatchEvent(new CustomEvent(CaptchaEvent.EXECUTE));
        });
    });
}

export default function initCaptchaInterceptor(): void {
    document.querySelectorAll<HTMLFormElement>('form').forEach((form) => {
        if (
            form.closest('dialog') ||
            form.getAttribute('is') === 'nh-login-form' ||
            form.getAttribute('is') === 'landlord-registration-form'
        ) {
            return;
        }

        const captchaInput = form.querySelector<HTMLInputElement>('[data-role="cf-turnstile"]');
        if (!captchaInput) {
            return;
        }

        if (form.dataset.captchaInterceptorInitialized === 'true') {
            return;
        }

        form.dataset.captchaInterceptorInitialized = 'true';

        initFormSubmitInterceptor(form, captchaInput);
        initSocialLoginInterceptor(form, captchaInput);
    });
}
